"use client";

import Link from "next/link";
import Footer from "@/components/marketing/footer";
import { useEffect, useState } from "react";

export default function SecurityPage() {
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const show = (id: string) => visibleSections.has(id);

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: "'Georgia', 'Times New Roman', serif", scrollBehavior: "smooth" }}>
      <style>{`html { scroll-behavior: smooth; }`}</style>

      {/* ─── NAV ─── */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrollY > 40 ? "bg-white shadow-md py-1" : "bg-white/90 py-1.5"
      }`}>
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <Link href="/" className="flex items-center cursor-pointer">
            <img src="/logo.png" alt="Contract Lucidity" className="h-12 md:h-14" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link href="/features" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Features</Link>
            <Link href="/how-it-works" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>How It Works</Link>
            <Link href="/law-firms" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Law Firms</Link>
            <Link href="/in-house" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>In-House</Link>
            <Link href="/security" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Security</Link>
            <Link href="/why-contract-lucidity" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Why Us</Link>
            <a href="https://lucidvault.contractlucidity.com" target="_blank" rel="noopener noreferrer" className="text-sm text-[#C8A84B] hover:text-[#b8993f] transition-colors font-semibold" style={{ fontFamily: "system-ui, sans-serif" }}>Lucid Vault</a>
            <Link
              href="/login"
              className="px-6 py-2.5 text-sm font-semibold bg-[#1B3A6B] text-white rounded hover:bg-[#2E5EA0] transition-colors"
              style={{ fontFamily: "system-ui, sans-serif" }}
            >
              Sign In
            </Link>
          </div>
          {/* Mobile hamburger */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2" aria-label="Toggle menu">
            <svg className="w-6 h-6 text-[#1B3A6B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              }
            </svg>
          </button>
        </div>
        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg" style={{ fontFamily: "system-ui, sans-serif" }}>
            <div className="flex flex-col px-6 py-4 gap-3">
              <Link href="/features" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Features</Link>
              <Link href="/how-it-works" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">How It Works</Link>
              <Link href="/law-firms" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Law Firms</Link>
              <Link href="/in-house" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">In-House</Link>
              <Link href="/security" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Security</Link>
              <Link href="/why-contract-lucidity" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Why Us</Link>
              <a href="https://lucidvault.contractlucidity.com" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)} className="text-sm text-[#C8A84B] font-semibold hover:text-[#b8993f] py-2 border-b border-gray-50">Lucid Vault</a>
              <Link href="/login" onClick={() => setMobileMenuOpen(false)} className="text-sm font-semibold text-white bg-[#1B3A6B] rounded px-4 py-2.5 text-center mt-2">Sign In</Link>
            </div>
          </div>
        )}
      </nav>

      {/* ─── HERO ─── */}
      <section className="relative pt-20 min-h-[480px] flex items-center justify-center overflow-hidden">
        <img
          src="/images/security-hero.webp"
          alt="Security and deployment infrastructure"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-[#1B3A6B]/80" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 py-24 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl leading-[1.15] mb-6 text-white">
            Your contracts. Your infrastructure.<br />
            <span className="text-[#C8A84B]">Your control.</span>
          </h1>
          <p className="text-lg md:text-xl text-white/70 leading-relaxed max-w-2xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
            Contract Lucidity is built for organizations where data sovereignty isn't optional.
          </p>
        </div>
      </section>

      {/* ─── SELF-HOSTED DEPLOYMENT ─── */}
      <section id="self-hosted" data-animate className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`grid md:grid-cols-2 gap-12 items-center transition-all duration-700 ${show("self-hosted") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div>
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>Infrastructure</p>
              <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-6">Self-Hosted Deployment</h2>
              <ul className="space-y-4" style={{ fontFamily: "system-ui, sans-serif" }}>
                {[
                  "Full Docker Compose deployment — 5 containers, one command",
                  "Runs on your infrastructure: AWS, Azure, GCP, or on-premise",
                  "No data leaves your network",
                  "PostgreSQL with pgvector for embeddings — no external vector database required",
                  "Redis for task queue — all processing stays internal",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-500 leading-relaxed">
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-[#C8A84B] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg border border-gray-100">
              <img src="/images/security-enterprise.webp" alt="Enterprise deployment infrastructure" className="w-full h-72 object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* ─── AI PROVIDER FLEXIBILITY ─── */}
      <section id="ai-provider" data-animate className="py-24 bg-[#F8F9FB]">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`grid md:grid-cols-2 gap-12 items-center transition-all duration-700 ${show("ai-provider") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="rounded-xl overflow-hidden shadow-lg border border-gray-100 md:order-1 order-2">
              <img src="/images/security-lock.webp" alt="Security and encryption" className="w-full h-72 object-cover" loading="lazy" />
            </div>
            <div className="md:order-2 order-1">
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>Flexibility</p>
              <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-6">AI Provider Flexibility</h2>
              <ul className="space-y-4" style={{ fontFamily: "system-ui, sans-serif" }}>
                {[
                  "Choose your AI provider: Anthropic (Claude) or OpenAI (GPT-4/5)",
                  "Switch providers without re-architecting",
                  "Use Azure OpenAI Service for enterprise compliance",
                  "API keys stay in your environment — never transmitted to Contract Lucidity",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-500 leading-relaxed">
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-[#C8A84B] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FILE INTEGRITY VALIDATION ─── */}
      <section id="file-integrity" data-animate className="py-24 bg-[#1B3A6B]">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("file-integrity") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="text-center mb-12">
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>Validation</p>
              <h2 className="text-3xl md:text-4xl text-white mb-4">File Integrity Validation</h2>
              <p className="text-white/50 max-w-2xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
                Every uploaded document validated before entering the system.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { format: "DOCX", detail: "ZIP structure verified, XML parsed, content confirmed" },
                { format: "PDF", detail: "Header validation" },
                { format: "RTF", detail: "Format verification" },
                { format: "Images", detail: "Magic byte validation (JPEG, PNG, GIF, TIFF)" },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`bg-white/5 border border-white/10 rounded-lg p-6 transition-all duration-700 ${
                    show("file-integrity") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                  }`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <h3 className="text-lg text-[#C8A84B] mb-2" style={{ fontFamily: "Georgia, serif" }}>{item.format}</h3>
                  <p className="text-sm text-white/60 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{item.detail}</p>
                </div>
              ))}
            </div>
            <div className={`mt-10 text-center transition-all duration-700 delay-500 ${show("file-integrity") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <p className="text-white/40 text-sm" style={{ fontFamily: "system-ui, sans-serif" }}>
                Corrupt or malformed files rejected with descriptive error messages.
              </p>
              <p className="text-white/70 text-lg mt-4">
                Garbage in, garbage out — <span className="text-[#C8A84B]">we don't accept garbage.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── ARCHITECTURE OVERVIEW ─── */}
      <section id="architecture" data-animate className="py-24 bg-[#F8F9FB]">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-700 ${show("architecture") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>Architecture</p>
            <h2 className="text-3xl md:text-4xl text-[#1B3A6B]">Architecture Overview</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { component: "Frontend", tech: "Next.js (React)", description: "Modern, responsive web application" },
              { component: "Backend", tech: "FastAPI (Python)", description: "Async API server with hot-reload" },
              { component: "Worker", tech: "Celery", description: "Async document processing pipeline" },
              { component: "Database", tech: "PostgreSQL + pgvector", description: "Relational storage with vector embeddings" },
              { component: "Cache", tech: "Redis", description: "Task queue and session management" },
              { component: "Storage", tech: "Volume-mounted", description: "File storage on your infrastructure" },
            ].map((item, i) => (
              <div
                key={i}
                className={`bg-white rounded-lg p-8 border border-gray-100 transition-all duration-700 ${
                  show("architecture") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <h3 className="text-lg text-[#1B3A6B] mb-1" style={{ fontFamily: "Georgia, serif" }}>{item.component}</h3>
                <p className="text-sm font-semibold text-[#C8A84B] mb-2" style={{ fontFamily: "system-ui, sans-serif" }}>{item.tech}</p>
                <p className="text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{item.description}</p>
              </div>
            ))}
          </div>
          <div className={`mt-8 text-center transition-all duration-700 delay-500 ${show("architecture") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-sm text-gray-400" style={{ fontFamily: "system-ui, sans-serif" }}>
              All components containerized and orchestrated via Docker Compose.
            </p>
          </div>
        </div>
      </section>

      {/* ─── COMPLIANCE ROADMAP ─── */}
      <section id="compliance" data-animate className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-700 ${show("compliance") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>Compliance</p>
            <h2 className="text-3xl md:text-4xl text-[#1B3A6B]">Compliance Roadmap</h2>
          </div>
          <div className={`space-y-4 transition-all duration-700 ${show("compliance") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {[
              { item: "SOC 2 Type II certification", status: "planned" },
              { item: "ISO 27001", status: "planned" },
              { item: "GDPR-ready architecture", status: "current", note: "Data stays on your infrastructure" },
              { item: "CCPA compliant", status: "current" },
              { item: "OIDC Single Sign-On", status: "current", note: "Azure AD, Okta, Google Workspace, and more" },
              { item: "SCIM 2.0 provisioning", status: "current", note: "Automatic user and group sync" },
              { item: "Role-based access control", status: "current", note: "Admin and user roles with group-based project access" },
              { item: "Ethical walls / information barriers", status: "current", note: "ABA Model Rule 1.10 — deny-overrides-allow enforcement" },
            ].map((entry, i) => (
              <div
                key={i}
                className={`flex items-center gap-4 p-5 rounded-lg border transition-all duration-700 ${
                  entry.status === "current"
                    ? "bg-[#1B3A6B]/5 border-[#1B3A6B]/20"
                    : "bg-white border-gray-100"
                } ${show("compliance") ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <span
                  className={`shrink-0 px-3 py-1 text-xs font-semibold rounded-full uppercase tracking-wide ${
                    entry.status === "current"
                      ? "bg-[#22C55E]/10 text-[#22C55E]"
                      : "bg-gray-100 text-gray-400"
                  }`}
                  style={{ fontFamily: "system-ui, sans-serif" }}
                >
                  {entry.status}
                </span>
                <span className="text-[#1B3A6B]" style={{ fontFamily: "system-ui, sans-serif" }}>{entry.item}</span>
                {entry.note && (
                  <span className="text-sm text-gray-400 ml-auto hidden md:inline" style={{ fontFamily: "system-ui, sans-serif" }}>
                    {entry.note}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ENTERPRISE IDENTITY MANAGEMENT ─── */}
      <section id="identity" data-animate className="py-24 bg-[#F8F9FB]">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("identity") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="text-center mb-12">
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>Identity</p>
              <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-4">Enterprise Identity Management</h2>
              <p className="text-gray-500 max-w-2xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
                Integrate with your existing identity provider. Provision users automatically. Control access at the project level.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "OIDC Single Sign-On", detail: "Azure AD / Entra ID, Okta, Google Workspace, and any OIDC-compliant provider" },
                { title: "SCIM 2.0 Provisioning", detail: "Automatic user and group sync from your identity provider — no manual account creation" },
                { title: "Role-Based Access Control", detail: "Admin and user roles with distinct permissions for platform management and daily use" },
                { title: "Group-Based Project Access", detail: "Assign groups to projects so team members see only what they need" },
                { title: "Seed Admin Protection", detail: "Protected initial administrator account that cannot be deleted or demoted" },
                { title: "Client Secret Encryption", detail: "OIDC client secrets encrypted at rest — never stored in plaintext" },
                { title: "REST API (OAuth 2.0)", detail: "126+ versioned endpoints at /api/v1/. OAuth client credentials flow for machine-to-machine access. Power Platform, ServiceNow, and custom integrations." },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`bg-white rounded-lg p-8 border border-gray-100 transition-all duration-700 ${
                    show("identity") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                  }`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <h3 className="text-lg text-[#1B3A6B] mb-2" style={{ fontFamily: "Georgia, serif" }}>{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── ETHICAL WALLS / INFORMATION BARRIERS ─── */}
      <section id="ethical-walls" data-animate className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("ethical-walls") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="text-center mb-12">
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>Information Barriers</p>
              <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-4">Ethical Walls</h2>
              <p className="text-gray-500 max-w-2xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
                Enforce information barriers at the platform level. Designed for ABA Model Rule 1.10 compliance with a deny-overrides-allow permission model.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "Deny-Overrides-Allow Model", detail: "iManage-style hybrid enforcement. Deny rules take absolute precedence over any allow grant, eliminating accidental exposure through inherited permissions." },
                { title: "No Admin Bypass", detail: "Even platform administrators cannot view documents in walled projects. The barrier is enforced at the query level with no override mechanism." },
                { title: "Matter-Level Isolation", detail: "Walls are applied at the project level. Every document, analysis, and report within a walled project is invisible to barred users." },
                { title: "Complete Audit Trail", detail: "Every access decision — granted and denied — is logged with user, resource, wall rule, and timestamp. Exportable for court or regulatory defense." },
                { title: "Conflicts Counsel Workflow", detail: "Create a wall, assign the attorneys who are barred, assign the projects that are restricted. One screen, immediate enforcement." },
                { title: "ABA Model Rule 1.10", detail: "Purpose-built for imputed disqualification screening. Ensure that conflicted attorneys are fully isolated from affected matters across the platform." },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`bg-[#F8F9FB] rounded-lg p-8 border border-gray-100 transition-all duration-700 ${
                    show("ethical-walls") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                  }`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <h3 className="text-lg text-[#1B3A6B] mb-2" style={{ fontFamily: "Georgia, serif" }}>{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── DOCUMENT LIFECYCLE SECURITY ─── */}
      <section id="lifecycle" data-animate className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("lifecycle") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="text-center mb-12">
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>End-to-End Security</p>
              <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-4">
                Secure the entire document lifecycle.
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
                Contract Lucidity secures analysis on your infrastructure. Lucid Vault secures document exchange with your clients.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-[#F8F9FB] rounded-lg p-8 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 rounded-full bg-[#1B3A6B] text-white flex items-center justify-center text-xs font-bold" style={{ fontFamily: "system-ui, sans-serif" }}>CL</span>
                  <h3 className="text-lg text-[#1B3A6B]" style={{ fontFamily: "Georgia, serif" }}>Analysis Security</h3>
                </div>
                <ul className="space-y-3" style={{ fontFamily: "system-ui, sans-serif" }}>
                  {["Self-hosted — data never leaves your network", "Fernet encryption for API keys at rest", "Non-root containers with dropped capabilities", "Rate limiting and audit logging on every endpoint"].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-500">
                      <span className="text-[#1B3A6B] mt-0.5 shrink-0">&#10003;</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#F8F9FB] rounded-lg p-8 border-2 border-[#C8A84B]">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 rounded-full bg-[#C8A84B] text-white flex items-center justify-center text-xs font-bold" style={{ fontFamily: "system-ui, sans-serif" }}>LV</span>
                  <h3 className="text-lg text-[#1B3A6B]" style={{ fontFamily: "Georgia, serif" }}>Exchange Security</h3>
                  <span className="ml-auto px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#C8A84B]/10 text-[#C8A84B]" style={{ fontFamily: "system-ui, sans-serif" }}>New</span>
                </div>
                <ul className="space-y-3" style={{ fontFamily: "system-ui, sans-serif" }}>
                  {["AES-256-GCM encryption at rest, TLS 1.3 in transit", "Single-tenant isolation — no shared databases", "White-labeled portal under your domain", "Complete audit trail for every file action"].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-500">
                      <span className="text-[#C8A84B] mt-0.5 shrink-0">&#10003;</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <a
                  href="https://lucidvault.contractlucidity.com"
                  rel="noopener noreferrer"
                  className="inline-block mt-6 px-5 py-2 text-sm font-semibold bg-[#C8A84B] text-white rounded hover:bg-[#b8993f] transition-colors"
                  style={{ fontFamily: "system-ui, sans-serif" }}
                >
                  Explore Lucid Vault
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section id="cta" data-animate className="py-24 bg-[#C8A84B]">
        <div className="max-w-3xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("cta") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-white rounded-xl border-2 border-[#1B3A6B] p-12 text-center shadow-lg">
              <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-4">
                Deploy on your terms.
              </h2>
              <p className="text-gray-500 mb-10 max-w-xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
                Self-hosted. Air-gapped capable. Your infrastructure, your AI provider, your data. No exceptions.
              </p>
              <a
                href="/demo"
                className="inline-block px-10 py-4 text-sm font-semibold bg-[#1B3A6B] text-white rounded hover:bg-white hover:text-[#1B3A6B] hover:ring-2 hover:ring-[#1B3A6B] transition-all"
                style={{ fontFamily: "system-ui, sans-serif" }}
              >
                Request a Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
